import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Check } from "lucide-react";
import api from "@/lib/api";

function Dot({ state }) {
  if (state === "complete")
    return <span className="mrow__dot dot--complete" aria-label="Completed"><Check size={13} strokeWidth={3} /></span>;
  if (state === "current")
    return <span className="mrow__dot dot--current" aria-label="Current" />;
  return <span className="mrow__dot dot--todo" aria-label="Not started" />;
}

export default function PathOverview() {
  const { pid, rid } = useParams();
  const { setCrumbs } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get(`/catalog/${pid}/${rid}`), api.get(`/progress/${pid}/${rid}`)])
      .then(([d, pr]) => {
        setData(d.data);
        setProgress(pr.data);
        setCrumbs([
          { label: d.data.product.name, to: `/${pid}` },
          { label: d.data.role.name, to: `/${pid}/${rid}` },
        ]);
        window.scrollTo(0, 0);
      })
      .catch(() => navigate("/", { replace: true }));
  }, [pid, rid, navigate, setCrumbs]);

  useEffect(() => { setData(null); setProgress(null); load(); }, [load]);

  if (!data || !progress) return <div className="loading">Loading the path…</div>;

  const completed = new Set(progress.completed);
  const keyFor = (si, mi) => `${pid}|${rid}|${si}|${mi}`;

  // current = first not-completed module in order
  let currentKey = null;
  outer: for (let si = 0; si < data.sections.length; si++) {
    for (let mi = 0; mi < data.sections[si].modules.length; mi++) {
      if (!completed.has(keyFor(si, mi))) { currentKey = keyFor(si, mi); break outer; }
    }
  }
  const moduleState = (si, mi) => {
    const k = keyFor(si, mi);
    if (completed.has(k)) return "complete";
    if (k === currentKey) return "current";
    return "todo";
  };

  const done = progress.doneCount;
  const total = progress.moduleCount;
  const certified = progress.final?.passed;
  let resumeLabel = "Start the path";
  if (certified) resumeLabel = "Review the certificate";
  else if (done >= total) resumeLabel = "Take the assessment";
  else if (done > 0) resumeLabel = `Resume — ${done} of ${total} done`;

  const onResume = () => {
    if (certified) return navigate(`/certificate/${pid}/${rid}`);
    if (currentKey) {
      const [, , si, mi] = currentKey.split("|").map((x, i) => (i >= 2 ? Number(x) : x));
      const sec = data.sections[si];
      return navigate(`/${pid}/${rid}/${sec.id}/${sec.modules[mi].id}`);
    }
    return navigate(`/${pid}/${rid}/final`);
  };

  return (
    <section className="page" data-testid="path-overview">
      <div className="container">
        {data.roleTabs.length > 1 && (
          <div className="tabs" data-testid="role-tabs">
            {data.roleTabs.map((t) => (
              <button key={t.id} className={`tab ${t.id === rid ? "tab--active" : ""}`} onClick={() => navigate(`/${pid}/${t.id}`)} data-testid={`role-tab-${t.id}`}>
                {t.name}
              </button>
            ))}
          </div>
        )}

        <div className="phead" data-testid="path-header">
          <div className="eyebrow-accent">{data.product.tag} · Certification path</div>
          <h1 className="phead__title">{data.role.name} Certification</h1>
          <p className="phead__blurb">{data.role.pathBlurb}</p>
          <div className="phead__row">
            <span className="chip-lg chip--outline">{data.role.hours}</span>
            <span className="chip-lg chip--outline">{data.role.moduleCount} modules</span>
            <span className="chip-lg chip--essential">{data.role.essentialCount} essential</span>
            <span className="chip-lg chip--meta" style={{ textTransform: "capitalize" }}>{data.role.device}</span>
            <button className="btn btn--dark phead__resume" onClick={onResume} data-testid="resume-btn">{resumeLabel}</button>
          </div>
        </div>

        <div className="grid-sections" data-testid="sections">
          {data.sections.map((s, si) => {
            const checkDone = !!progress.checks[s.id];
            return (
              <div key={s.id} className="scard" data-testid={`section-${s.id}`}>
                <div className="scard__head">
                  <span className="scard__num">{String(si + 1).padStart(2, "0")}</span>
                  <span className="scard__title">{s.title}</span>
                  {s.essential && <span className="etag">Essential</span>}
                  <span className="scard__mins">{s.minutes}</span>
                </div>
                <div className="scard__list">
                  {s.modules.map((m, mi) => {
                    const st = moduleState(si, mi);
                    return (
                      <button key={m.id} className={`mrow mrow--${st}`} onClick={() => navigate(`/${pid}/${rid}/${s.id}/${m.id}`)} data-testid={`module-row-${m.id}`}>
                        <Dot state={st} />
                        <span className="mrow__title">{m.title}</span>
                        <span className="mrow__dur">{m.duration}</span>
                      </button>
                    );
                  })}
                  <button className={`mrow mrow--dashed ${checkDone ? "mrow--complete" : ""}`} onClick={() => navigate(`/${pid}/${rid}/${s.id}/check`)} data-testid={`knowledge-check-${s.id}`}>
                    <Dot state={checkDone ? "complete" : "todo"} />
                    <span className="mrow__title">Knowledge check</span>
                    <span className="mrow__dur">{s.questionCount} Q</span>
                  </button>
                </div>
              </div>
            );
          })}

          <div className="final-card" data-testid="final-card">
            <div className="final-card__eyebrow">FINAL</div>
            <div className="final-card__title">Final assessment</div>
            <p className="final-card__body">
              {data.finalAssessment.questions} questions drawn from the essential workflows. {data.finalAssessment.passMark}% to certify. Two retries, then a 24-hour wait.
            </p>
            <button className="btn btn--yellow" style={{ marginTop: "auto", alignSelf: "flex-start" }} onClick={() => navigate(certified ? `/certificate/${pid}/${rid}` : `/${pid}/${rid}/final`)} data-testid="final-assessment-btn">
              {certified ? "Review the certificate" : "Take the assessment"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
